import { Provider as ReduxProvider, useSelector } from "react-redux";
import configureStore from "configureStore";
import { BulkAssetUploadDataProvider } from "features/CreativeLibrary/BulkCreativeUploader/BulkCreativeUploaderDataProvider";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import * as ReactRouterDom from "react-router-dom";
import { lookup } from "lib/redux";
import { BulkEditLineItemsContextProvider } from "frontier/lib/instruction/campaign/lineItem/bulkEdit/BulkEditContext";
import { ActivationContextProvider } from "frontier/lib/plan/activation/ActivationContext";
import { AudienceDataProvider } from "hooks/useAudienceData";
import { selectCurrentUser } from "appReducers/authReducer";
import { FC, PropsWithChildren } from "react";
import Auth from "./Auth";
import CrashScreen from "./CrashScreen";
import { Footer } from "./Footer/Footer";
import SmithersRouter from "./Router/SmithersRouter";
import ToasterProvider from "./Toaster/Toaster";
import SmithersToolbar from "./Toolbar/SmithersToolbar";
import { ApplicationSettingsProvider } from "./ApplicationSettingsProvider";
import OrganizationSettings from "./OrganizationSettings";
import { madTracker } from "../frontier/lib/tracker";
import { setMadTracker } from "@madhive/kit-plus";

if (typeof window !== "undefined") {
  // Expose React Router DOM for use in @madhive/kit-ui
  window.RouterUtilsKit = ReactRouterDom;
  // Set the madTracker instance for use in @madhive/kit-plus
  setMadTracker(madTracker);
}

const store = configureStore();
lookup.store = store;

/**
 * Inner component that uses Redux hooks to key the AudienceDataProvider.
 * Must be inside ReduxProvider to access the store.
 */
const AudienceProviderWrapper: FC<PropsWithChildren> = ({ children }) => {
  // Get current user to key the AudienceDataProvider
  // This ensures the provider unmounts/remounts on user change (login/logout)
  // which clears all cached audience/segment data and prevents data leakage between users
  const currentUser = useSelector(selectCurrentUser);

  return (
    <AudienceDataProvider key={currentUser?.id}>
      {children}
    </AudienceDataProvider>
  );
};

const SmithersBrowserRouter: FC = () => (
  <ReduxProvider store={store}>
    <ToasterProvider>
      <Auth>
        <OrganizationSettings>
          <ApplicationSettingsProvider>
            <AudienceProviderWrapper>
              <BulkEditLineItemsContextProvider>
                <ActivationContextProvider>
                  <BulkAssetUploadDataProvider>
                    <SmithersToolbar />
                    <CrashScreen>
                      <SmithersRouter />
                    </CrashScreen>
                  </BulkAssetUploadDataProvider>
                </ActivationContextProvider>
              </BulkEditLineItemsContextProvider>
            </AudienceProviderWrapper>
            <Footer />
          </ApplicationSettingsProvider>
        </OrganizationSettings>
      </Auth>
    </ToasterProvider>
  </ReduxProvider>
);

const router = createBrowserRouter([
  { path: "*", element: <SmithersBrowserRouter /> }
]);

const Smithers: FC = () => (
  <RouterProvider
    router={router}
    /**
     * The react-router code that suppresses upgrade warnings looks for `v7_relativeSplatPath`
     * on this object (the typing seems to erroneously omit it though):
     * https://github.com/remix-run/react-router/blob/948d00bbbd82e402b8835eba7e5810ce523a26df/packages/react-router/lib/deprecations.ts#L34
     */
    //@ts-ignore
    future={{ v7_startTransition: false, v7_relativeSplatPath: false }}
  />
);

export default Smithers;
